// The saved customer behind the phone typed in the delivery details (UC-18).
//
// The till asks once the phone reads as a number, and shows nothing new until
// the answer comes: a stranger's number and a slow network look the same, and
// neither may move the fields under the cashier's fingers.
//
// Saving happens on Confirm, apart from the order, and says nothing either way.
// The order never waits for the customer; a failed save is tried again on the
// next Confirm. A manager's corrections are the only calls here that report back.

import { api } from '../api/client';
import type { components } from '../api/types';

export type Customer = components['schemas']['CustomerBody'];
export type Suggestion = components['schemas']['CustomerSuggestionBody'];

/** The fewest digits the server suggests customers for (SuggestMinDigits). */
export const SUGGEST_FROM = 5;

/**
 * The saved customers whose number holds these digits, or null when the till
 * could not ask. Fewer than SUGGEST_FROM digits suggest nobody without asking.
 */
export async function suggestCustomers(digits: string): Promise<Suggestion[] | null> {
	if (digits.length < SUGGEST_FROM) return [];
	try {
		const { data, error } = await api.GET('/api/till/customers/suggest', {
			params: { query: { digits } },
		});
		if (error !== undefined || !data) return null;
		return data.customers ?? [];
	} catch {
		return null;
	}
}

// The number the answer is for, and the answer; customer null is nobody.
let known = $state<{ phone: string; customer: Customer | null } | null>(null);
let asking = '';
// Every look asks the server, because the owner may have corrected or deleted
// the customer since; the last answer stays on screen until the new one comes.
// Saves are counted, so a screen still showing the number asks again after
// one without waiting to be reopened.
let saves = $state(0);

/** Goes up with every save that went through. */
export function customerSaves(): number {
	return saves;
}

/** The saved customer for this normalized phone, once the server has said. */
export function knownCustomer(phone: string): Customer | null {
	if (!known || known.phone !== phone || !known.customer) return null;
	const hidden = removal?.customer.id === known.customer.id ? removal.addressId : '';
	if (!hidden) return known.customer;
	return {
		...known.customer,
		addresses: (known.customer.addresses ?? []).filter((a) => a.id !== hidden),
	};
}

// A manager's address removal waiting out its Undo: gone from the till at
// once, and sent to the server only when the Undo runs out. It stays hidden
// while the request is on its way, so it never flickers back.
let removal = $state<{
	customer: Customer;
	addressId: string;
	address: string;
	sent: boolean;
} | null>(null);

/** The address removal the Undo is showing for, if any. */
export function pendingAddressRemoval(): { address: string } | null {
	return removal && !removal.sent ? removal : null;
}

/**
 * Whether an address removal waits out its Undo or is on its way. A reload
 * then would forget it or cut its request off (INT-7).
 */
export function addressRemovalUnsettled(): boolean {
	return removal !== null;
}

/** Remove now, undoably. A removal still waiting is sent first. */
export function removeAddressWithUndo(customer: Customer, addressId: string): void {
	const address = (customer.addresses ?? []).find((a) => a.id === addressId);
	if (!address) return;
	void commitAddressRemoval();
	removal = { customer, addressId, address: address.address, sent: false };
}

/** Undo: the address comes back and the server never hears of it. */
export function undoAddressRemoval(): void {
	if (removal && !removal.sent) removal = null;
}

/** The Undo ran out: tell the server. A failure brings the address back. */
export async function commitAddressRemoval(): Promise<EditOutcome | null> {
	const waiting = removal;
	if (!waiting || waiting.sent) return null;
	removal = { ...waiting, sent: true };
	const result = await removeCustomerAddress(waiting.customer, waiting.addressId);
	if (removal?.addressId === waiting.addressId) removal = null;
	return result;
}

/** Ask who this is. Returns the customer, or null for nobody or no answer. */
export async function lookUpCustomer(phone: string): Promise<Customer | null> {
	if (!phone || asking === phone) return knownCustomer(phone);
	asking = phone;
	try {
		const { data, error } = await api.GET('/api/till/customers/lookup', {
			params: { query: { phone } },
		});
		// A newer number was typed while this one was on its way.
		if (asking !== phone || error !== undefined || !data) return null;
		known = { phone, customer: data.customer ?? null };
		return known.customer;
	} catch {
		return null;
	} finally {
		if (asking === phone) asking = '';
	}
}

export interface Details {
	phone: string;
	name: string;
	address: string;
	landmark: string;
	deliveryAreaId: string;
}

/** Confirm: keep the customer and the address. Silent, and never awaited. */
export async function saveCustomer(details: Details): Promise<void> {
	try {
		const { error } = await api.POST('/api/till/customers/save', {
			body: {
				phone: details.phone,
				...(details.name.trim() ? { name: details.name.trim() } : {}),
				...(details.address.trim() ? { address: details.address.trim() } : {}),
				...(details.landmark.trim() ? { landmark: details.landmark.trim() } : {}),
				...(details.deliveryAreaId ? { deliveryAreaId: details.deliveryAreaId } : {}),
			},
		});
		// A screen showing this number asks again and sees the new address.
		if (error === undefined) saves++;
	} catch {
		// Said nothing on purpose: the next Confirm saves again.
	}
}

export type EditOutcome = 'saved' | 'gone' | 'failed';

function outcome(status: number): EditOutcome {
	if (status === 404) return 'gone';
	return status >= 200 && status < 300 ? 'saved' : 'failed';
}

/** A manager corrects the name. */
export async function renameCustomer(customer: Customer, name: string): Promise<EditOutcome> {
	try {
		const { response } = await api.PATCH('/api/till/customers/{customerId}', {
			params: { path: { customerId: customer.id } },
			body: { name },
		});
		const result = outcome(response.status);
		if (result === 'saved' && known?.customer?.id === customer.id) {
			known = { phone: known.phone, customer: { ...known.customer, name } };
		}
		if (result === 'gone') known = null;
		return result;
	} catch {
		return 'failed';
	}
}

export interface AddressEdit {
	address: string;
	landmark: string;
	deliveryAreaId: string;
}

/** What an address edit came to: taken is another address of theirs. */
export type AddressEditOutcome = EditOutcome | 'taken';

/** A manager corrects one saved address in place. */
export async function editCustomerAddress(
	customer: Customer,
	addressId: string,
	edit: AddressEdit,
): Promise<AddressEditOutcome> {
	try {
		const { response, error } = await api.PATCH(
			'/api/till/customers/{customerId}/addresses/{addressId}',
			{
				params: { path: { customerId: customer.id, addressId } },
				body: {
					address: edit.address.trim(),
					...(edit.landmark.trim() ? { landmark: edit.landmark.trim() } : {}),
					...(edit.deliveryAreaId ? { deliveryAreaId: edit.deliveryAreaId } : {}),
				},
			},
		);
		if (response.status === 422 && error?.details?.path === 'address') return 'taken';
		const result = outcome(response.status);
		if (result === 'saved' && known?.customer?.id === customer.id) {
			const edited = {
				address: edit.address.trim(),
				landmark: edit.landmark.trim() || undefined,
				areaId: edit.deliveryAreaId || undefined,
			};
			known = {
				phone: known.phone,
				customer: {
					...known.customer,
					addresses: (known.customer.addresses ?? []).map((a) =>
						a.id === addressId ? { ...a, ...edited } : a,
					),
				},
			};
		}
		if (result === 'gone') known = null;
		return result;
	} catch {
		return 'failed';
	}
}

/** A manager removes one saved address. */
export async function removeCustomerAddress(
	customer: Customer,
	addressId: string,
): Promise<EditOutcome> {
	try {
		const { response } = await api.POST(
			'/api/till/customers/{customerId}/addresses/{addressId}/remove',
			{ params: { path: { customerId: customer.id, addressId } } },
		);
		const result = outcome(response.status);
		if (result === 'saved' && known?.customer?.id === customer.id) {
			known = {
				phone: known.phone,
				customer: {
					...known.customer,
					addresses: (known.customer.addresses ?? []).filter((a) => a.id !== addressId),
				},
			};
		}
		if (result === 'gone') known = null;
		return result;
	} catch {
		return 'failed';
	}
}
